import _ from 'lodash';

import { getUUID } from 'utils/uuid';

import { ConstructionBudgetStatus, ConstructionType } from 'store/construction/types';

export const makeEmptyBudgetLine = () => ({
  key: getUUID(),
  category: null,
  description: '',
  budgetedAmount: '',
});

export const getInitialCreateBudgetDraft = () => ({
  constructionType: ConstructionType.GROUND_UP,
  contingencyAmount: '',
  lines: [makeEmptyBudgetLine()],
});

// A create line counts only when it has both a category and a positive amount — that is the set that
// gets POSTed and the Save button gates on there being at least one.
export const getHasCompleteCreateLine = ({ lines = [] }) =>
  lines.some(line => !!line.category && Number(line.budgetedAmount) > 0);

// Builds the full ConstructionBudgetDto for a brand-new budget: only complete lines are sent, order is
// the row position, the total is derived from the line amounts, and the budget starts Active so it is
// immediately usable for draws.
export const toCreateBudgetPayload = ({ draft = {} }) => {
  const { lines = [], constructionType, contingencyAmount } = draft;

  const lineItems = lines
    .filter(line => !!line.category && Number(line.budgetedAmount) > 0)
    .map((line, index) => ({
      category: line.category,
      description: line.description ?? '',
      budgetedAmount: Number(line.budgetedAmount) || 0,
      order: index,
    }));

  return {
    totalBudgetAmount: _.sumBy(lineItems, 'budgetedAmount'),
    contingencyAmount: contingencyAmount === '' ? null : Number(contingencyAmount) || 0,
    status: ConstructionBudgetStatus.ACTIVE,
    constructionType: constructionType ?? ConstructionType.GROUND_UP,
    isConstructionToPermanent: false,
    lineItems,
  };
};

export const getInitialBudgetDraft = (budgetLines = []) =>
  budgetLines.reduce((draft, line) => {
    draft[line.id] = line.budgeted ?? '';

    return draft;
  }, {});

// The Loans API budget endpoint round-trips the full ConstructionBudgetDto (scalar fields + every line
// item with its category / description / order), so an edit must PUT the whole budget back — not just the
// changed amounts. The total is re-derived from the line amounts so it stays consistent with the edits.
export const toBudgetPayload = ({ budget = null, budgetLines = [], draft = {} }) => {
  const lineItems = budgetLines.map(line => ({
    id: line.id,
    category: line.category,
    description: line.description,
    budgetedAmount: Number(draft[line.id] ?? line.budgeted) || 0,
    order: line.order,
  }));

  return {
    totalBudgetAmount: _.sumBy(lineItems, 'budgetedAmount'),
    contingencyAmount: budget?.contingencyAmount ?? null,
    status: budget?.status,
    constructionType: budget?.constructionType,
    isConstructionToPermanent: budget?.isConstructionToPermanent ?? false,
    lineItems,
  };
};
